import type { SubtractionLevelParams } from '../progression/levels.js';
import { pickRandom, shuffle } from './random.js';

/**
 * Subtraction distractors are plausible-mistake values: the operands themselves,
 * difference±1, and A+B (an "added instead" mistake), clipped to the digit
 * range and excluding the true difference.
 */
export function pickSubtractionDistractors(
  a: number,
  b: number,
  digitMax: number,
  distractorCount: number,
  rng: () => number,
): number[] {
  if (distractorCount <= 0) return [];
  const diff = a - b;
  const candidates = new Set<number>();
  for (const v of [a, b, diff + 1, diff - 1, a + b]) {
    if (v >= 0 && v <= digitMax && v !== diff) candidates.add(v);
  }
  // Pad with neighbors of the difference if needed.
  for (const delta of [2, -2, 3, -3]) {
    if (candidates.size >= distractorCount) break;
    const v = diff + delta;
    if (v >= 0 && v <= digitMax && v !== diff) candidates.add(v);
  }
  const pool = Array.from(candidates);
  const distractors: number[] = [];
  while (distractors.length < distractorCount && pool.length > 0) {
    const idx = Math.floor(rng() * pool.length);
    const [picked] = pool.splice(idx, 1);
    distractors.push(picked!);
  }
  return distractors;
}

export interface SubtractionTrial {
  a: number;
  b: number;
  /** True difference (a − b). */
  diff: number;
  /** Digit-tile choices in display order. */
  tiles: number[];
  /** Indices of the A objects that should render as "removed" (length b). */
  removedIndices: number[];
}

export function generateSubtractionTrial(
  params: SubtractionLevelParams,
  rng: () => number = Math.random,
): SubtractionTrial {
  const { aMax } = params;
  // Build all valid (a, b) with 1 ≤ b ≤ a ≤ aMax. Constraint: b ≥ 1 (no
  // "subtract zero" trials — they're visually identical to no removal).
  const pairs: Array<[number, number]> = [];
  for (let a = 2; a <= aMax; a++) {
    for (let b = 1; b <= a; b++) {
      pairs.push([a, b]);
    }
  }
  const [a, b] = pickRandom(pairs, rng);
  const diff = a - b;
  const distractors = pickSubtractionDistractors(a, b, aMax, params.tileCount - 1, rng);
  const tiles = shuffle([diff, ...distractors], rng);

  // Pick which b of the a objects get marked removed (random subset, sorted
  // for stable rendering).
  const indices = Array.from({ length: a }, (_, i) => i);
  const removedIndices: number[] = [];
  while (removedIndices.length < b) {
    const idx = Math.floor(rng() * indices.length);
    const [picked] = indices.splice(idx, 1);
    removedIndices.push(picked!);
  }
  removedIndices.sort((x, y) => x - y);

  return { a, b, diff, tiles, removedIndices };
}
